import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { InputField } from '../components/ui/InputField';
import { AntiGravityBackground } from '../components/ui/AntiGravityBackground';
import loginBg from '../assets/login-bg.png';
import logo from '../assets/logo-versarc.png';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const success = await login(email, password);
        if (success) {
            navigate('/');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="min-h-screen w-full flex overflow-hidden bg-white">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-emba-dark text-white flex-col justify-between p-12 overflow-hidden">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center brightness-[0.4] grayscale-[0.2]"
                    style={{ backgroundImage: `url(${loginBg})` }}
                />
                <AntiGravityBackground />
                <div className="relative z-10">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                    >
                        <img src={logo} alt="Versarc" className="h-16 w-auto object-contain" />
                    </motion.div>
                </div>

                <div className="relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="font-serif text-6xl leading-tight mb-6"
                    >
                        Quality Exports <br /> & Global Trade
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.7 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="text-sm font-light tracking-wide max-w-md text-white/80"
                    >
                        Connecting you to a global marketplace of premium products and wholesale opportunities.
                        Sign in to explore our diverse catalog.
                    </motion.p>
                </div>

                <div className="relative z-10 text-xs tracking-widest text-white/40">
                    © 2026 SORAMMI ARC
                </div>
            </div>

            {/* Right Side - Functional */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-24 relative">
                {/* Mobile Background Particle Fix */}
                <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-white via-sky-50 to-fuchsia-50 -z-10" />

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full max-w-md space-y-12"
                >
                    <div className="text-center lg:text-left space-y-2">
                        <h2 className="font-serif text-3xl lg:text-4xl text-black">Welcome Back</h2>
                        <p className="text-gray-500 font-light">Please enter your details to sign in.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="space-y-6">
                            <InputField
                                label="Email Address"
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com"
                                required
                            />
                            <InputField
                                label="Password"
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <div className="space-y-4">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full"
                            >
                                {isSubmitting ? 'Signing in...' : 'Sign In'}
                            </Button>

                            <div className="flex justify-between items-center text-[10px] tracking-widest uppercase text-gray-400">
                                <Link to="/forgot-password" className="hover:text-black transition-colors">
                                    Forgot Password?
                                </Link>
                                <span>
                                    New here?
                                    <Link to="/register" className="text-black ml-2 hover:underline underline-offset-4 decoration-1">
                                        Create Account
                                    </Link>
                                </span>
                            </div>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default Login;
